import React from "react";
import _get from "lodash/get";

const DEFAULT_VALUES = {
  CALL_NUMBER: "N/A",
  BARCODE: "N/A",
  TITLE: "(No Title Available)",
  AUTHOR: "(No Author Available)",
};

export const getDocumentData = (document, formData) => {
  return document || formData.document || {};
};

export const getAuthors = (docData) => {
  return _get(docData, "metadata.authors", []);
};

export const hasOtherAuthors = (docData) => {
  return _get(docData, "metadata.other_authors", false);
};

export const getTitle = (docData, formData) => {
  return (
    formData.title || _get(docData, "metadata.title") || DEFAULT_VALUES.TITLE
  );
};

export const getCallNumberPrefix = (docData, formData) => {
  if (formData.callNumberPrefix) {
    return formData.callNumberPrefix;
  }

  if (formData.call_number_prefix) {
    return formData.call_number_prefix;
  }

  const prefix = _get(docData, "metadata.call_number_prefix");
  if (prefix && typeof prefix === "string" && prefix.trim()) {
    return prefix.trim();
  }

  return "";
};

export const getCallNumberSuffix = (docData, formData, selectedItem = null) => {
  if (selectedItem?.itemData?.call_number_suffix) {
    return selectedItem.itemData.call_number_suffix;
  }

  if (selectedItem?.callNumberSuffix) {
    return selectedItem.callNumberSuffix;
  }

  if (formData.call_number_suffix) {
    return formData.call_number_suffix;
  }

  return "";
};

export const parseCallNumber = (fullCallNumber) => {
  if (!fullCallNumber || fullCallNumber === "N/A") {
    return { prefix: "", base: "", suffix: "" };
  }

  const parts = fullCallNumber.trim().split(" ");

  if (parts.length >= 3) {
    return {
      prefix: parts[0],
      base: parts[1],
      suffix: parts.slice(2).join(" "),
    };
  } else if (parts.length === 2) {
    return {
      prefix: "",
      base: parts[0],
      suffix: parts[1],
    };
  } else {
    return {
      prefix: "",
      base: fullCallNumber,
      suffix: "",
    };
  }
};

export const getCallNumber = (docData, formData, selectedItem = null) => {
  const fullCallNumber = getBaseCallNumber(docData, formData);
  return fullCallNumber;
};

export const getBaseCallNumber = (docData, formData) => {
  if (formData.callNumber) {
    return formData.callNumber;
  }

  if (formData.call_number) {
    return formData.call_number;
  }

  const value = _get(docData, "metadata.call_number");
  if (value && typeof value === "string" && value.trim()) {
    return value.trim();
  }

  const identifiers = _get(docData, "metadata.identifiers", []);
  if (Array.isArray(identifiers)) {
    for (const identifier of identifiers) {
      if (identifier.scheme === "call_number") {
        return identifier.value;
      }
    }
  }

  return DEFAULT_VALUES.CALL_NUMBER;
};

export const getCallNumberBase = (docData, formData) => {
  const fullCallNumber = getBaseCallNumber(docData, formData);
  const parsed = parseCallNumber(fullCallNumber);
  return parsed.base || fullCallNumber;
};

export const getBarcode = (docData, formData, selectedItem = null) => {
  if (selectedItem?.barcode && selectedItem.barcode !== "N/A") {
    return selectedItem.barcode;
  }

  if (selectedItem?.itemData?.barcode) {
    return selectedItem.itemData.barcode;
  }

  if (selectedItem?.itemData?.rawItem?.barcode) {
    return selectedItem.itemData.rawItem.barcode;
  }

  if (
    selectedItem?.id &&
    selectedItem.id !== "default" &&
    !selectedItem.id.includes("unknown")
  ) {
    return selectedItem.id;
  }

  if (formData.barcode) {
    return formData.barcode;
  }

  return DEFAULT_VALUES.BARCODE;
};

export const renderAuthorsBackup = (authors, formData) => {
  if (Array.isArray(authors) && authors.length > 0) {
    return authors.map((author, index) => (
      <span key={index}>
        {author.full_name || author.name}
        {index < authors.length - 1 ? ", " : ""}
      </span>
    ));
  }

  return formData.author || DEFAULT_VALUES.AUTHOR;
};

export const validateDocumentFormData = (formData) => {
  const issues = [];

  if (!formData.call_number_prefix && formData.call_number) {
    issues.push(
      "Call number provided without prefix - this may cause display issues"
    );
  }

  if (formData.call_number_prefix && !formData.call_number) {
    issues.push("Call number prefix provided without call number");
  }

  return {
    isValid: issues.length === 0,
    issues,
    formData,
  };
};

export const validateItemFormData = (formData) => {
  const issues = [];

  if (
    formData.call_number_suffix &&
    typeof formData.call_number_suffix !== "string"
  ) {
    issues.push("Call number suffix should be a string");
  }

  return {
    isValid: issues.length === 0,
    issues,
    formData,
  };
};
